import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FavoriteItem, FavoritesState } from "./index.types";

const initialState: FavoritesState = {
  favoritePatients: [],
  favoriteProviders: [],
  favoriteLocations: [],
  newFavorites: {},
  favoritedActionItems: [],
  allFavorites: [],
};

const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    addFavoritePatient: (state, action: PayloadAction<string>) => {
      if (!state.favoritePatients.includes(action.payload)) {
        state.favoritePatients.push(action.payload);
      }
    },
    removeFavoritePatient: (state, action: PayloadAction<string>) => {
      state.favoritePatients = state.favoritePatients.filter((id) => id !== action.payload);
    },
    addFavoriteProvider: (state, action: PayloadAction<string>) => {
      if (!state.favoriteProviders.includes(action.payload)) {
        state.favoriteProviders.push(action.payload);
      }
    },
    removeFavoriteProvider: (state, action: PayloadAction<string>) => {
      state.favoriteProviders = state.favoriteProviders.filter((id) => id !== action.payload);
    },
    addFavoriteLocation: (state, action: PayloadAction<string>) => {
      if (!state.favoriteLocations.includes(action.payload)) {
        state.favoriteLocations.push(action.payload);
      }
    },
    removeFavoriteLocation: (state, action: PayloadAction<string>) => {
      state.favoriteLocations = state.favoriteLocations.filter((id) => id !== action.payload);
    },
    clearAllFavorites: (state) => {
      state.favoritePatients = [];
      state.favoriteProviders = [];
      state.favoriteLocations = [];
    },
    addNewFavorite: (state, action: PayloadAction<{ id: string | number; value: string | number }>) => {
      state.newFavorites[action.payload.id] = action.payload.value;
    },
    removeNewFavorite: (state, action: PayloadAction<string | number>) => {
      delete state.newFavorites[action.payload];
    },
    clearNewFavorites: (state) => {
      state.newFavorites = {};
    },
    addFavoritedActionItem: (state, action: PayloadAction<FavoriteItem>) => {
      const exists = state.favoritedActionItems.find((item) => item.id === action.payload.id);
      if (!exists) {
        state.favoritedActionItems.push(action.payload);
      }
    },
    removeFavoritedActionItem: (state, action: PayloadAction<FavoriteItem>) => {
      state.favoritedActionItems = state.favoritedActionItems.filter((item) => item.id !== action.payload.id);
    },
    clearFavoritedActionItems: (state) => {
      state.favoritedActionItems = [];
    },
    setAllFavorites: (state, action: PayloadAction<FavoriteItem[]>) => {
      state.allFavorites = action.payload;
    },
    addToAllFavorites: (state, action: PayloadAction<FavoriteItem>) => {
      const exists = state.allFavorites.find((item) => item.id === action.payload.id);
      if (!exists) {
        state.allFavorites.push(action.payload);
      }
    },
    removeFromAllFavorites: (state, action: PayloadAction<string | number>) => {
      state.allFavorites = state.allFavorites.filter((item) => item.id !== action.payload);
    },
    deleteFavorite: (state, action: PayloadAction<string | number>) => {
      // Remove from all favorites
      state.allFavorites = state.allFavorites.filter((item) => item.id !== action.payload);
      // Remove from new favorites
      delete state.newFavorites[action.payload];
      // Remove from favorited action items
      state.favoritedActionItems = state.favoritedActionItems.filter((item) => item.id !== action.payload);
    },
    resetFavorites: (state) => {
      state.newFavorites = {};
      state.favoritedActionItems = [];
    },
  },
});

export const {
  addFavoritePatient,
  removeFavoritePatient,
  addFavoriteProvider,
  removeFavoriteProvider,
  addFavoriteLocation,
  removeFavoriteLocation,
  clearAllFavorites,
  addNewFavorite,
  removeNewFavorite,
  clearNewFavorites,
  addFavoritedActionItem,
  removeFavoritedActionItem,
  clearFavoritedActionItems,
  setAllFavorites,
  addToAllFavorites,
  removeFromAllFavorites,
  deleteFavorite,
  resetFavorites,
} = favoritesSlice.actions;

// Alias for backward compatibility
export const addFavorite = addToAllFavorites;

// Selectors
export const selectFavoritePatients = (state: { favorites: FavoritesState }) => state.favorites.favoritePatients;

export const selectFavoriteProviders = (state: { favorites: FavoritesState }) => state.favorites.favoriteProviders;

export const selectFavoriteLocations = (state: { favorites: FavoritesState }) => state.favorites.favoriteLocations;

export const selectNewFavorites = (state: { favorites: FavoritesState }) => state.favorites.newFavorites;

export const selectFavoritedActionItems = (state: { favorites: FavoritesState }) => state.favorites.favoritedActionItems;

export const selectAllFavorites = (state: { favorites: FavoritesState }) => state.favorites.allFavorites;

export default favoritesSlice.reducer;
